import React, {
    useReducer,
    useRef,
    useEffect,
    type ReactNode,
    type KeyboardEvent,
    type MouseEvent
} from 'react';
import { isNil, isEmpty, noop, isEqual, isString } from 'lodash/fp';
import { autoUpdate, flip, shift, useFloating } from '@floating-ui/react';
import classnames from 'classnames/bind';
import { useTheme } from 'app/components/core/ThemeProvider';
import { useDeepEffect, useDefaultId } from 'app/lib/hooks';
import { matches } from 'app/lib/utils';
import { Input, InputSize } from 'app/components/core/Input';
import { Icon, IconSize } from 'app/components/core/Icon';
import { FieldLabel } from 'app/components/core/FieldLabel';
import { useErrorTooltip } from 'app/components/core/ErrorTooltip';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { Loading, LoadingSize } from 'app/components/core/Loading';

import * as styles from './ComboBox.css';

const cx = classnames.bind(styles);

export enum ComboBoxEdge {
    ROUNDED = 'rounded',
    FLAT = 'flat'
}

export enum ComboBoxSize {
    MEDIUM = 'medium',
    LARGE = 'large'
}

const SIZE_CLASSES: Record<ComboBoxSize, string> = {
    [ComboBoxSize.MEDIUM]: 'size-md',
    [ComboBoxSize.LARGE]: 'size-lg'
};

const INPUT_SIZE: Record<ComboBoxSize, InputSize> = {
    [ComboBoxSize.MEDIUM]: InputSize.MEDIUM,
    [ComboBoxSize.LARGE]: InputSize.LARGE
};

const EDGE_CLASSES: Record<ComboBoxEdge, string> = {
    [ComboBoxEdge.ROUNDED]: 'edge-rounded',
    [ComboBoxEdge.FLAT]: 'edge-flat'
};

interface ComboBoxOption {
    name: string;
    isLiteral?: boolean;
    error?: boolean;
    disabled?: boolean;
    help?: string;
}

interface SuggestionProps {
    name: string;
    title?: string;
    isHighlighted: boolean;
    onHover?: () => void;
    onClick: () => void;
    disabled?: boolean;
    children?: ReactNode;
}

function Suggestion({
    name,
    title,
    isHighlighted,
    onHover,
    onClick,
    disabled,
    children
}: SuggestionProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => {
            if (isHighlighted) {
                ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        });
    }, [isHighlighted]);

    return (
        <div
            ref={ref}
            title={title || name}
            className={styles.item}
            aria-selected={isHighlighted}
            aria-disabled={disabled}
            aria-label={name}
            onMouseOver={onHover}
            onMouseDown={(e: MouseEvent) => {
                e.preventDefault();
                if (!disabled) {
                    onClick();
                }
            }}
            role="option">
            {children ?? name}
        </div>
    );
}

interface SuggestionsProps {
    attachTo: string;
    children: ReactNode;
    className?: string;
}

const Suggestions = ({ attachTo, children, className }: SuggestionsProps) => {
    const parent = document.getElementById(attachTo);
    const width = parent?.getBoundingClientRect().width ?? 0;

    const { refs, elements, floatingStyles } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        elements: {
            reference: parent
        },
        middleware: [flip(), shift()]
    });

    useEffect(() => {
        try {
            elements.floating?.showPopover();
        } catch {
            // Ignore error. We might be trying to call this on an element
            // that's being removed from the DOM.
        }
    }, [elements.floating]);

    return (
        <div
            popover="manual"
            data-testid="combo-box-suggestions"
            className={cx('list', className)}
            style={{ ...floatingStyles, minWidth: width }}
            ref={refs.setFloating}
            role="listbox">
            {children}
        </div>
    );
};

interface ComboBoxState {
    value?: ComboBoxOption;
    hasSelectedValue: boolean;
    isDirty: boolean;
    filter: string;
    isEditing: boolean;
    highlightedIndex: number;
    options: ComboBoxOption[];
}

type ComboBoxAction =
    | { type: 'Open' }
    | { type: 'Blur' }
    | { type: 'OnChange'; value: string }
    | { type: 'HighlightItem'; index: number }
    | { type: 'ChooseItem'; index: number }
    | { type: 'Cancel' }
    | { type: 'SetValue'; value?: ComboBoxOption };

export interface ComboBoxProps {
    id?: string;
    testId?: string;
    name?: string;
    label?: ReactNode;
    placeholder?: string;
    value?: ComboBoxOption;
    options: ComboBoxOption[];
    error?: string | string[];
    warning?: string | string[];
    itemRenderer?: (item: ComboBoxOption, isOption: boolean) => ReactNode;
    allowCustomValue?: boolean;
    className?: string;
    size?: ComboBoxSize;
    edge?: ComboBoxEdge;
    autoFocus?: boolean;
    disabled?: boolean;
    isProcessing?: boolean;
    onChange?: (value?: ComboBoxOption) => void;
    onEdit?: (isEditing: boolean) => void;
    readOnly?: boolean;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}

export function ComboBox({
    id,
    testId,
    name,
    label,
    placeholder,
    value,
    options,
    error,
    warning,
    itemRenderer,
    allowCustomValue = false,
    className,
    size = ComboBoxSize.LARGE,
    edge = ComboBoxEdge.FLAT,
    autoFocus = false,
    disabled = false,
    isProcessing = false,
    onChange = noop,
    onEdit = noop,
    readOnly = false,
    ...props
}: ComboBoxProps) {
    const { themeClassname } = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const inputId = useDefaultId('combo-box', id);
    const attachmentId = useDefaultId('combo-box-attachment');
    const hasError = Boolean(error?.length);
    const hasWarning = !hasError && Boolean(warning?.length);
    const hasLabel = !isEmpty(label);
    const isDisabled = disabled || isProcessing;

    const keepFocus = () =>
        requestAnimationFrame(() => {
            ref.current?.querySelector('div')?.focus();
        });

    const [state, dispatch] = useReducer(
        (state: ComboBoxState, action: ComboBoxAction): ComboBoxState => {
            switch (action.type) {
                case 'Open':
                    return {
                        ...state,
                        hasSelectedValue: false,
                        isEditing: true,
                        highlightedIndex: options.findIndex(({ name }) => name === value?.name),
                        options
                    };
                case 'Blur': {
                    const filterInOptions = options.find((x) => x.name === state.filter);

                    if (allowCustomValue && state.filter.length > 0) {
                        return {
                            ...state,
                            hasSelectedValue: true,
                            isDirty: false,
                            isEditing: false,
                            value: filterInOptions ?? state.options[0]
                        };
                    }

                    if (filterInOptions) {
                        return {
                            ...state,
                            hasSelectedValue: true,
                            isDirty: false,
                            isEditing: false,
                            value: filterInOptions
                        };
                    }

                    return {
                        ...state,
                        hasSelectedValue: false,
                        isDirty: false,
                        filter: value?.name ?? '',
                        isEditing: false
                    };
                }
                case 'OnChange': {
                    const filteredOptions = options.filter((o) => matches(o.name, action.value));

                    if (allowCustomValue && !isEmpty(action.value)) {
                        filteredOptions.unshift({ name: action.value, isLiteral: true });
                    }

                    return {
                        ...state,
                        hasSelectedValue: false,
                        isDirty: true,
                        filter: action.value,
                        options: filteredOptions,
                        highlightedIndex: filteredOptions.length > 0 ? 0 : -1,
                        isEditing: true
                    };
                }
                case 'HighlightItem':
                    return {
                        ...state,
                        hasSelectedValue: false,
                        isEditing: true,
                        highlightedIndex: action.index
                    };
                case 'ChooseItem': {
                    const value = state.options[action.index]!;
                    const filteredOptions = options.filter((o) => matches(o.name, value.name));
                    const canSelectValue = !value.error;

                    if (allowCustomValue) {
                        filteredOptions.unshift({ name: value.name, isLiteral: true });
                    }

                    keepFocus();

                    return {
                        ...state,
                        hasSelectedValue: canSelectValue,
                        isDirty: false,
                        isEditing: false,
                        filter: value?.name ?? '',
                        value,
                        options: filteredOptions,
                        highlightedIndex: !allowCustomValue || value?.isLiteral ? 0 : 1
                    };
                }
                case 'Cancel':
                    keepFocus();
                    return {
                        ...state,
                        hasSelectedValue: false,
                        filter: '',
                        value: undefined,
                        isEditing: false,
                        highlightedIndex: 0,
                        options
                    };
                case 'SetValue': {
                    if (isNil(action.value)) {
                        return {
                            ...state,
                            hasSelectedValue: false,
                            value: undefined,
                            filter: '',
                            highlightedIndex: -1,
                            options
                        };
                    }

                    const filteredOptions = options.filter((o) =>
                        matches(o.name, action.value!.name)
                    );

                    if (allowCustomValue) {
                        filteredOptions.unshift({ name: action.value.name, isLiteral: true });
                    }

                    const index = isNil(value)
                        ? -1
                        : filteredOptions.findIndex(
                              (option) =>
                                  option.name === value.name && option.isLiteral === value.isLiteral
                          );

                    return {
                        ...state,
                        hasSelectedValue: false,
                        value,
                        options: filteredOptions,
                        filter: action.value.name,
                        highlightedIndex: index
                    };
                }
                default:
                    return state;
            }
        },
        {
            value,
            hasSelectedValue: false,
            isDirty: false,
            filter: value?.name ?? '',
            isEditing: false,
            highlightedIndex: -1,
            options
        }
    );

    function keyDownHandler(event: KeyboardEvent<HTMLDivElement | HTMLInputElement>) {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                if (state.highlightedIndex > 0) {
                    dispatch({ type: 'HighlightItem', index: state.highlightedIndex - 1 });
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (state.highlightedIndex < state.options.length - 1) {
                    dispatch({ type: 'HighlightItem', index: state.highlightedIndex + 1 });
                }
                break;
            case 'Escape':
                event.preventDefault();
                event.stopPropagation();
                dispatch({ type: 'Cancel' });
                break;
            case 'Enter': {
                event.preventDefault();
                if (state.highlightedIndex >= 0) {
                    dispatch({ type: 'ChooseItem', index: state.highlightedIndex });
                }
                break;
            }
            default:
                if (!readOnly && !state.isEditing) {
                    dispatch({ type: 'Open' });
                }
                break;
        }
    }

    useDeepEffect(() => {
        if (state.hasSelectedValue && !isEqual(state.value, value) && !isProcessing) {
            onChange(state.value);
        }
    }, [state.value, state.hasSelectedValue, onChange, value, isProcessing]);

    useDeepEffect(() => {
        dispatch({ type: 'SetValue', value });
    }, [value]);

    useEffect(() => {
        onEdit(state.isEditing);

        if (state.isEditing) {
            ref.current?.querySelector('input')?.select();
        }
    }, [onEdit, state.isEditing]);

    const baseClasses = cx(
        'base',
        SIZE_CLASSES[size],
        EDGE_CLASSES[edge],
        themeClassname,
        { hasLabel, hasError, hasWarning },
        className
    );

    const isShowingSuggestions = state.isEditing && !isEmpty(state.options);
    const { ErrorTooltip, refs, getReferenceProps, getTooltipProps } = useErrorTooltip();

    const comboboxRef = refs.reference as React.MutableRefObject<HTMLDivElement>;
    useEffect(() => {
        if (autoFocus && comboboxRef.current) {
            setTimeout(() => comboboxRef.current?.focus(), 0);
        }
    }, [comboboxRef, autoFocus]);

    const MaybeIcon = () => {
        const { isEditing, filter } = state;

        if (isProcessing) {
            return <Loading className={styles.icon} size={LoadingSize.SMALL} />;
        }
        if (!isEditing) {
            return (
                <Icon
                    className={cx('icon', 'arrow')}
                    variant="dropdown-open"
                    size={IconSize.EXTRA_SMALL}
                />
            );
        }
        if (!isEmpty(filter)) {
            return (
                <Button
                    icon="close-small"
                    variant={ButtonVariant.GHOST}
                    size={ButtonSize.EXTRA_SMALL}
                    className={styles.icon}
                    onClick={() => dispatch({ type: 'OnChange', value: '' })}
                    onMouseDown={(e) => e.preventDefault()}
                />
            );
        }

        return null;
    };
    const isShowingIcon = !isNil(MaybeIcon());

    return (
        <div ref={ref} className={baseClasses}>
            {hasLabel && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
            <div id={attachmentId}>
                {!state.isEditing ? (
                    <div
                        id={inputId}
                        data-testid={testId}
                        role="combobox"
                        aria-label={isString(label) ? label : undefined}
                        tabIndex={0}
                        className={cx('pill', { disabled: isDisabled, hasIcon: isShowingIcon })}
                        aria-disabled={isDisabled}
                        aria-invalid={hasError || hasWarning}
                        aria-placeholder={placeholder}
                        {...getReferenceProps({
                            onClick: () => !isDisabled && !readOnly && dispatch({ type: 'Open' }),
                            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) =>
                                !isDisabled && keyDownHandler(e),
                            ...props
                        })}>
                        {value && itemRenderer && itemRenderer(value, false)}
                        {value && !itemRenderer && <span title={value.name}>{value.name}</span>}
                        {!value && (
                            <span className={styles.placeholder} title={placeholder}>
                                {placeholder}
                            </span>
                        )}
                    </div>
                ) : (
                    <Input
                        role="combobox"
                        id={inputId}
                        testId={testId}
                        name={name}
                        value={state.filter}
                        onChange={(value) => dispatch({ type: 'OnChange', value: value as string })}
                        onBlur={() => dispatch({ type: 'Blur' })}
                        onKeyDown={keyDownHandler}
                        size={INPUT_SIZE[size]}
                        error={error}
                        warning={warning}
                        className={styles.input}
                    />
                )}
            </div>
            <MaybeIcon />
            {isShowingSuggestions ? (
                <Suggestions attachTo={attachmentId} className={SIZE_CLASSES[size]}>
                    {state.options.map((item, index) => (
                        <Suggestion
                            key={`${item.name}-${item.isLiteral}`}
                            name={item.name}
                            title={item.help}
                            disabled={item.disabled}
                            isHighlighted={index === state.highlightedIndex}
                            onHover={
                                !item.disabled
                                    ? () => dispatch({ type: 'HighlightItem', index })
                                    : undefined
                            }
                            onClick={() => dispatch({ type: 'ChooseItem', index })}>
                            {itemRenderer ? itemRenderer(item, true) : null}
                        </Suggestion>
                    ))}
                </Suggestions>
            ) : (
                <ErrorTooltip error={error} warning={warning} {...getTooltipProps()} />
            )}
        </div>
    );
}
